import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import useAxios from '../utils/useAxios';
import { Features, ModuleKey } from '../utils/features';
import { ShellContext } from './layout/AppShell';
import { SectionCard } from './ui';
import { errorText } from './OrderLines';
import { TRUE_COLORS } from '../theme';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  cafeteria: <LocalCafeOutlinedIcon />, tienda: <StorefrontOutlinedIcon />, encargos: <ShoppingCartOutlinedIcon />,
  servicios: <ContentCutIcon />, otro: <CategoryOutlinedIcon />,
};
const MODULE_ICONS: Record<ModuleKey, React.ReactNode> = {
  orders: <ShoppingBagOutlinedIcon />, encargos: <LocalShippingOutlinedIcon />, loyalty: <LoyaltyOutlinedIcon />,
  promotions: <DiscountOutlinedIcon />, inventory: <Inventory2OutlinedIcon />, options: <TuneOutlinedIcon />,
  balance: <AccountBalanceWalletOutlinedIcon />, kitchen: <ReceiptLongOutlinedIcon />,
};
/** Where each module is set up. */
// Where each module is set up (kitchen tickets have nothing to set: they print at the register).
const MODULE_PAGES: Partial<Record<ModuleKey, { to: string; label: string }>> = {
  orders: { to: '/admin/orders', label: 'Pedidos' }, encargos: { to: '/admin/orders', label: 'Pedidos' },
  loyalty: { to: '/admin/loyalty', label: 'Lealtad' }, promotions: { to: '/admin/promotions', label: 'Promociones' },
  inventory: { to: '/admin/inventory', label: 'Inventario' }, options: { to: '/admin/modifiers', label: 'Opciones' },
  balance: { to: '/admin/balance', label: 'Saldo' },
};

/**
 * "Tu negocio" in Configuración: the kind of business (which turns on its usual modules) and each module on or off.
 * Off hides it from the dashboard and its forms; nothing is deleted, and a module in use stays on until it's turned off
 * in its own page.
 */
const BusinessFeatures = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { membership, features, setFeatures } = React.useContext(ShellContext);
  const isAdmin = !!membership?.is_admin;
  const [draft, setDraft] = React.useState<{ business_type: string; modules: Record<string, boolean> } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (features) setDraft({ business_type: features.business_type, modules: Object.fromEntries(features.modules.map(m => [m.key, m.chosen])) });
  }, [features]);
  if (!features || !draft) return null;

  const type = features.types.find(t => t.key === draft.business_type);
  const changed = draft.business_type !== features.business_type || features.modules.some(m => m.chosen !== draft.modules[m.key]);
  const chooseType = (key: string) => {
    const preset = features.types.find(t => t.key === key);
    setSaved(false);
    setDraft({ business_type: key, modules: Object.fromEntries(features.modules.map(m => [m.key, !!preset?.modules.includes(m.key)])) });
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.put('management/organization/features/', draft);
      setFeatures?.(data as Features);
      setSaved(true);
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Tu negocio" subtitle="Elige qué tipo de negocio tienes y qué funciones usas: el panel muestra solo esas. Ocultar algo no borra nada.">
      <Stack spacing={3}>
        {!isAdmin && <Alert severity="info">Solo un administrador puede cambiar las funciones del negocio.</Alert>}
        {!features.configured && isAdmin && (
          <Alert severity="info">Hoy ves todas las funciones. Elige tu tipo de negocio para quedarte solo con las que usas.</Alert>
        )}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {saved && !changed && <Alert severity="success" onClose={() => setSaved(false)}>Listo: el menú de la izquierda ya muestra solo lo que usas.</Alert>}

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Tipo de negocio</Typography>
          <Grid container spacing={1.5}>
            {features.types.map(t => {
              const selected = draft.business_type === t.key;
              return (
                <Grid key={t.key} size={{ xs: 12, sm: 6, md: 4, lg: 'grow' }}>
                  <ButtonBase onClick={() => chooseType(t.key)} disabled={!isAdmin} data-testid={`type-${t.key}`}
                    sx={{ width: '100%', height: '100%', textAlign: 'left', alignItems: 'flex-start', justifyContent: 'flex-start', p: 1.5, borderRadius: 2,
                      border: '2px solid', borderColor: selected ? TRUE_COLORS.primary : TRUE_COLORS.borderSolid,
                      bgcolor: selected ? 'rgba(20,37,44,0.04)' : 'background.paper', gap: 1.25 }}>
                    <Box sx={{ color: TRUE_COLORS.primary, mt: 0.25 }}>{TYPE_ICONS[t.key]}</Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.label}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t.description}</Typography>
                    </Box>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Funciones</Typography>
          <Stack divider={<Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}` }} />}>
            {features.modules.map(m => {
              const on = !!draft.modules[m.key] || !!m.in_use;
              return (
                <Stack key={m.key} direction="row" spacing={1.5} data-testid={`module-${m.key}`} sx={{ py: 1.5, alignItems: 'center' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', flexShrink: 0,
                    bgcolor: 'rgba(20,37,44,0.06)', color: on ? TRUE_COLORS.primary : TRUE_COLORS.secondary }}>
                    {MODULE_ICONS[m.key]}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.label}</Typography>
                      {type?.modules.includes(m.key) && <Chip size="small" label="Recomendado" variant="outlined" />}
                      {m.in_use && <Chip size="small" color="success" label="En uso" />}
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {m.in_use ? `${m.in_use} Para ocultarlo, primero apágalo en ${MODULE_PAGES[m.key]?.label ?? m.label}.` : m.description}
                    </Typography>
                  </Box>
                  {on && MODULE_PAGES[m.key] && (
                    <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate(MODULE_PAGES[m.key]!.to)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                      Configurar
                    </Button>
                  )}
                  <Switch checked={on} disabled={!isAdmin || !!m.in_use} slotProps={{ input: { 'aria-label': m.label } }}
                    onChange={e => { setSaved(false); setDraft(d => (d ? { ...d, modules: { ...d.modules, [m.key]: e.target.checked } } : d)); }} />
                </Stack>
              );
            })}
          </Stack>
        </Box>

        {isAdmin && (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={!changed || busy} data-testid="save-features">{busy ? 'Guardando…' : 'Guardar'}</Button>
            {changed && <Button onClick={() => setDraft({ business_type: features.business_type, modules: Object.fromEntries(features.modules.map(m => [m.key, m.chosen])) })}>Descartar</Button>}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default BusinessFeatures;
